import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { X, Maximize2, Mail, MessageSquare, LogIn, Rocket, LifeBuoy } from 'lucide-react';
import PublicChatWidget from '@/components/PublicChatWidget';
import SupportContactForm from '@/components/SupportContactForm';

// EXCEPTION LIST — narrowly scoped (owner directive 2026-06-22).
// The shared mobile bottom navigation renders on ALL standard authenticated
// customer routes (dashboard, cards, reports, billing, settings, consent, …).
// It is intentionally suppressed ONLY on a few focused / regulated flows where
// a persistent global nav would sit over the flow's own primary CTAs or invite
// the user to wander mid-transaction. On these the bar is replaced by a single
// small launcher and the full-screen chat is force-minimized. Each exception
// below has a stated UX/security reason — NOT merely "different layout".
const SENSITIVE_SEGMENTS = [
  'close-account',     // account deletion / Danger Zone — destructive, full-screen
                       // focused flow; global nav would overlay Back/Confirm-Delete CTAs.
  'recovery/meisai',   // secure MEISAI credential reveal — security-sensitive, must stay focused.
  'account/recovery',  // account recovery — authentication/recovery flow.
];
// NOTE: billing/settings/consent were previously suppressed only because the
// OLD 4-color bar overlapped page CTAs. The current neutral bar + global
// `pb-20` content clearance removes that risk, so they now render the nav.

export default function StickySupport() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const ja = lang === 'ja';
  const [emailOpen, setEmailOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [escalationPrefill, setEscalationPrefill] = useState(null);

  const isSensitive = SENSITIVE_SEGMENTS.some(
    (seg) => (pathname || '').toLowerCase().includes(seg));

  // The full-screen chat stays minimized by default on sensitive flows so it
  // can never overlay account-deletion / recovery / billing / security CTAs.
  const chatVisible = chatOpen && !isSensitive;

  // Allow other components (chat widget) to trigger the escalation form via window event.
  useEffect(() => {
    const handler = (e) => {
      setEscalationPrefill(e.detail || null);
      setEmailOpen(true);
    };
    window.addEventListener('jtr:open-support', handler);
    return () => window.removeEventListener('jtr:open-support', handler);
  }, []);

  const closeEmailPanel = () => {
    setEmailOpen(false);
    // Clear escalation prefill after the panel closes so the next manual open is empty.
    setTimeout(() => setEscalationPrefill(null), 200);
  };

  return (
    <>
      {/* Desktop: Right-side vertical tabs anchored to the edge — hidden on mobile.
          Pinned to the right edge (rounded-l) and vertically centered so they
          never cover table columns, row actions, filters, or pagination. */}
      <button onClick={() => setChatOpen(!chatOpen)}
        className="fixed bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg transition-all z-[9998] hidden lg:flex flex-col gap-2 px-2.5 py-5 rounded-l-xl hover:px-3 right-0 top-[30%] -translate-y-1/2 items-center justify-center"
        data-testid="sticky-chat-btn"
        data-floating-control="true"
        title={ja ? 'ソラに質問' : 'Ask Sora'}
        aria-label={ja ? 'ソラに質問' : 'Ask Sora'}>
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
          <rect x="4" y="6" width="16" height="14" rx="4" stroke="currentColor" strokeWidth="2"/>
          <line x1="12" y1="6" x2="12" y2="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="12" cy="2" r="1" fill="currentColor"/>
          <circle cx="9" cy="12" r="1.5" fill="currentColor"/>
          <circle cx="15" cy="12" r="1.5" fill="currentColor"/>
          <path d="M9 16h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span className="text-sm font-bold leading-tight tracking-wider" style={{ writingMode: 'vertical-lr' }}>{ja ? 'ソラに質問' : 'ASK SORA'}</span>
      </button>

      <button onClick={() => { setEscalationPrefill(null); setEmailOpen(true); }}
        className="fixed bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-all z-[9997] hidden lg:flex flex-col gap-2 px-2.5 py-5 rounded-l-xl hover:px-3 right-0 top-[calc(30%+80px)] items-center justify-center"
        data-testid="sticky-email-btn"
        data-floating-control="true"
        aria-label="Email Support - Need Help?">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
          <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"/>
          <rect x="2" y="4" width="20" height="16" rx="2"/>
        </svg>
        <span className="text-base font-bold leading-tight tracking-wider" style={{ writingMode: 'vertical-lr' }}>{ja ? 'メールサポート' : 'EMAIL SUPPORT'}</span>
        <span className="text-sm font-bold leading-tight tracking-wider text-blue-200" style={{ writingMode: 'vertical-lr' }}>{ja ? 'お困りですか？' : 'NEED HELP?'}</span>
      </button>

      {/* Mobile: Bottom sticky bar — 4 equal quarter-width buttons.
          Order: Support · Try Free/Dashboard · Early Access · Chat.
          Stacked icon + tiny label so all four fit cleanly on narrow phones.
          HIDDEN on sensitive flows (account deletion / recovery / billing /
          security) where it would cover the page's bottom CTAs — replaced
          there by a single small minimized launcher (below). */}
      {!isSensitive && (
      <>
      {/* PPA-T07 (Professional Polish Audit, 2026-02): auto-hide this bar in
          short LANDSCAPE viewports only (e.g. a phone rotated to
          844x390) — at that aspect ratio the 56px bar alone consumes ~14%
          of the already-short vertical space. Portrait phones and
          landscape tablets (which have plenty of height) are unaffected;
          this does NOT change the intentional "render on every standard
          authenticated route" behavior (owner directive 2026-06-22).
      */}
      <button onClick={() => { setEscalationPrefill(null); setEmailOpen(true); }}
        data-mobile-bottom-bar
        className="fixed z-[9997] bottom-0 left-0 right-3/4 h-14 flex flex-col items-center justify-center gap-1 bg-white border-t border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition-colors lg:hidden [@media(orientation:landscape)_and_(max-height:500px)]:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
        data-active={emailOpen} data-testid="sticky-support-mobile-email">
        <Mail className="w-[19px] h-[19px] text-slate-400" strokeWidth={2} />
        <span className="text-[10px] font-semibold tracking-tight leading-none truncate max-w-full px-0.5 text-slate-500">{ja ? 'サポート' : 'Support'}</span>
      </button>
      <Link to={user ? ((user.role === 'admin' || user.role === 'host') ? '/admin' : '/dashboard') : `/${lang}/free-trial`}
        data-mobile-bottom-bar
        className="fixed z-[9997] bottom-0 left-1/4 right-2/4 h-14 flex flex-col items-center justify-center gap-1 bg-white border-t border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition-colors lg:hidden [@media(orientation:landscape)_and_(max-height:500px)]:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
        data-active={(pathname||"").startsWith("/dashboard")||(pathname||"").startsWith("/admin")} data-testid="sticky-support-mobile-login"
        data-print-hide="true">
        <LogIn className="w-[19px] h-[19px] text-slate-400" strokeWidth={2} />
        <span className="text-[10px] font-semibold tracking-tight leading-none truncate max-w-full px-0.5 text-slate-500">{user ? (ja ? 'マイページ' : 'Dashboard') : (ja ? '無料で試す' : 'Try Free')}</span>
      </Link>
      <Link to={`/${lang}/early-access`}
        data-mobile-bottom-bar
        className="fixed z-[9997] bottom-0 left-2/4 right-1/4 h-14 flex flex-col items-center justify-center gap-1 bg-white border-t border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition-colors lg:hidden [@media(orientation:landscape)_and_(max-height:500px)]:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
        data-active={(pathname||"").includes("early-access")} data-testid="sticky-support-mobile-early-access">
        <Rocket className="w-[19px] h-[19px] text-slate-400" strokeWidth={2} />
        <span className="text-[10px] font-semibold tracking-tight leading-none truncate max-w-full px-0.5 text-slate-500">{ja ? '先行登録' : 'Early Access'}</span>
      </Link>
      <button onClick={() => setChatOpen(!chatOpen)}
        data-mobile-bottom-bar
        className="fixed z-[9998] bottom-0 right-0 left-3/4 h-14 flex flex-col items-center justify-center gap-1 bg-white border-t border-slate-200 hover:bg-slate-50 active:bg-slate-100 transition-colors lg:hidden [@media(orientation:landscape)_and_(max-height:500px)]:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom,0px)' }}
        data-active={chatOpen} data-testid="sticky-support-mobile-chat">
        <MessageSquare className="w-[19px] h-[19px] text-slate-400" strokeWidth={2} />
        <span className="text-[10px] font-semibold tracking-tight leading-none truncate max-w-full px-0.5 text-slate-500">{ja ? 'チャット' : 'Chat'}</span>
      </button>
      </>
      )}

      {/* Sensitive flows: a single small minimized launcher (mobile only) that
          opens human/email support WITHOUT covering the page's bottom CTAs.
          The controlled sensitive pages add bottom padding so this never
          overlaps their primary action. */}
      {isSensitive && (
        <button
          onClick={() => { setEscalationPrefill(null); setEmailOpen(true); }}
          className="fixed z-[9996] bottom-3 right-3 h-9 pl-2.5 pr-3 rounded-full flex items-center gap-1.5 bg-slate-800/90 backdrop-blur hover:bg-slate-900 text-white shadow-lg lg:hidden"
          data-testid="sticky-support-minimized-launcher"
          data-floating-control="true"
          aria-label={ja ? 'サポート' : 'Support'}>
          <LifeBuoy className="w-4 h-4" strokeWidth={2} />
          <span className="text-[11px] font-bold">{ja ? 'サポート' : 'Support'}</span>
        </button>
      )}

      {/* Chat Widget */}
      <PublicChatWidget isOpen={chatVisible} onClose={() => setChatOpen(false)} />

      {/* Email Support Panel */}
      {emailOpen && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-end p-0 sm:p-4" data-testid="email-support-panel">
          <div className="absolute inset-0 bg-black/30" onClick={closeEmailPanel} />
          <div className="relative bg-white w-full sm:w-[440px] sm:max-h-[92vh] sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex-1 flex justify-center">
                <img src="/assets/logo/japantollreceipts.gif" alt="Japan Toll Receipts" className="h-16 w-auto object-contain" />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button className="p-1.5 text-gray-400 hover:text-gray-600" aria-label="maximize"><Maximize2 className="w-4 h-4" strokeWidth={1.5} /></button>
                <button onClick={closeEmailPanel} className="p-1.5 text-gray-400 hover:text-gray-600" data-testid="close-email-panel" aria-label="close"><X className="w-5 h-5" strokeWidth={1.5} /></button>
              </div>
            </div>
            <div className="px-5 py-3 overflow-y-auto">
              {escalationPrefill ? (
                <p className="text-xs text-gray-600 mb-2" data-testid="email-panel-escalation-intro">
                  {ja
                    ? 'ご質問をサポートチームにお繋ぎします。'
                    : "We'll forward your chat question to support."}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mb-1">{ja ? 'いつでもお気軽にお問い合わせください。すぐに対応いたします。' : "Ask anytime, and we'll respond promptly."} <span className="text-red-500">*</span> {ja ? '必須' : 'Required'}</p>
              )}
              <div className="mt-1">
                <SupportContactForm
                  layout="panel"
                  prefill={escalationPrefill || {}}
                  sourcePage={escalationPrefill ? 'chat_widget' : 'sticky_email_panel'}
                  onSubmitted={() => { /* keep panel open to show success */ }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
